/**
 * 統一的 API 響應處理工具
 * 提供統一的 API 響應格式驗證和處理
 */

import { logService } from './logService';

import { IApiResponse, IApiErrorResponse, IApiSuccessResponse } from '@/types/api';
import { ApiErrorCode, IApiError, IRtkQueryError } from '@/types/errors';

/**
 * 驗證 API 響應格式
 */
export function validateApiResponse<T>(response: unknown): response is IApiResponse<T> {
  if (!response || typeof response !== 'object') {
    return false;
  }
  
  const apiResponse = response as IApiResponse<T>;
  return 'success' in apiResponse && typeof apiResponse.success === 'boolean';
}

/**
 * 檢查 API 響應是否成功
 */
export function isApiSuccessResponse<T>(response: unknown): response is IApiSuccessResponse<T> {
  return validateApiResponse<T>(response) && response.success === true;
}

/**
 * 檢查 API 響應是否為錯誤
 */
export function isApiErrorResponse(response: unknown): response is IApiErrorResponse {
  return validateApiResponse(response) && response.success === false;
}

/**
 * 提取 API 響應數據
 */
export function extractApiData<T>(response: IApiResponse<T> | undefined | null): T | null {
  if (!response) {
    return null;
  }
  if (response.success && response.data !== null && response.data !== undefined) {
    return response.data;
  }
  return null;
}

/**
 * 提取 API 錯誤訊息
 */
export function extractApiErrorMessage(response: IApiErrorResponse | IRtkQueryError): string {
  // 處理 RTK Query 錯誤格式
  if ('data' in response && response.data) {
    const errorData = response.data as IApiErrorResponse;
    if (errorData.msg) {
      return errorData.msg;
    }
    if (errorData.message) {
      return errorData.message;
    }
  }
  
  // 處理標準 API 錯誤格式
  if ('msg' in response && response.msg) {
    return response.msg;
  }
  
  if ('message' in response && response.message) {
    return response.message;
  }
  
  // 處理網絡錯誤
  if ('error' in response && response.error) {
    return response.error;
  }
  
  return '操作失敗，請稍後再試';
}

/**
 * 提取 API 錯誤碼
 */
export function extractApiErrorCode(response: IApiErrorResponse | IRtkQueryError): string {
  if ('data' in response && response.data && typeof response.data === 'object') {
    const errorData = response.data as IApiErrorResponse;
    if (errorData.code) {
      return errorData.code;
    }
  }
  
  if ('code' in response && response.code) {
    return response.code;
  }
  
  return ApiErrorCode.INTERNAL_ERROR;
}

/**
 * 提取 API 錯誤狀態碼
 */
export function extractApiErrorStatus(response: IApiErrorResponse | IRtkQueryError): number {
  if ('data' in response && response.data && typeof response.data === 'object') {
    const errorData = response.data as IApiErrorResponse;
    if (errorData.status) {
      return errorData.status;
    }
  }
  
  if ('status' in response && response.status) {
    return response.status;
  }
  
  return 500;
}

/**
 * 創建標準化的 API 錯誤對象
 */
export function createApiError(
  response: IApiErrorResponse | IRtkQueryError,
  endpoint?: string
): IApiError {
  const code = extractApiErrorCode(response);
  const status = extractApiErrorStatus(response);
  const message = extractApiErrorMessage(response);
  
  const error: IApiError = {
    code,
    status,
    message,
    timestamp: new Date().toISOString(),
  };
  
  if (endpoint) {
    error.details = { endpoint };
  }
  
  // 記錄錯誤日誌
  logService.error('API Error', error, { endpoint });
  
  return error;
}

/**
 * 處理 API 響應
 */
export function handleApiResponse<T>(
  response: unknown,
  endpoint?: string
): { success: boolean; data: T | null; error: IApiError | null } {
  if (isApiSuccessResponse<T>(response)) {
    return {
      success: true,
      data: extractApiData<T>(response),
      error: null,
    };
  }
  
  if (isApiErrorResponse(response)) {
    return {
      success: false,
      data: null,
      error: createApiError(response, endpoint),
    };
  }
  
  // 處理未知格式的響應
  logService.warn('Unknown API response format', { response, endpoint });
  
  return {
    success: false,
    data: null,
    error: {
      code: ApiErrorCode.INTERNAL_ERROR,
      status: 500,
      message: '未知的響應格式',
      timestamp: new Date().toISOString(),
    },
  };
}

